import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '@paypalcorp/pp-react';
import { isNodeChatAlreadyExists, isPrivacyWebFormEnabledInRC } from '../../lib/globalHelper';
import Page from '../../lib/page';
import i18n from '../../lib/reacti18n';
import { initiateAsyncChat, isMessageUsAvailable } from '../../lib/asyncMessageHelper';
import { modalChannels } from '../constants/UtilVariables';
import { asyncOrigins } from '../../common/commonConstants';
import EmailForm from './EmailForm';
import ContactOption from '../components/ContactOption';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import { loadModalContainer, loginFromEmailPage } from '../actions/action-login-modal';
import dispatchLogin from '../actions/action-login-redirect';
import { CONTACT_OPTION } from '../constants/PropTypesShapes';
import { showEmailWebForm } from '../actions/action-email-no-intent';

const emailLabels = {
  WEB_FORM_HEADER: i18n.message('smarthelp/emailUs.emailUs.webFormHeader'),
  LOGIN_INSTRUCTIONS: i18n.message('smarthelp/emailUs.emailUs.loginInstructions', { loginUrl: '' }),
  MESSAGE_US_INSTRUCTIONS: i18n.message('smarthelp/emailUs.emailUs.messageUsInstructions', { messageUsUrl: '' }),
  HELP_CENTER_INSTRUCTIONS: i18n.message('smarthelp/emailUs.emailUs.helpCenterInstructions', {
    helpCenterUrl: '/smarthelp/home',
    contactUsUrl: '/smarthelp/contact-us',
  }),
  LOGIN_TEXT: i18n.message('smarthelp/emailUs.emailUs.login'),
  NO_ACCOUNT_INSTRUCTIONS: i18n.message('smarthelp/emailUs.emailUs.noAccountInstructions'),
  MESSAGE_HEADER: i18n.message('smarthelp/contactOption.messageUs.header'),
  MESSAGE_TEXT: i18n.message('smarthelp/contactOption.messageUs.text'),
  ERRORS: i18n.message('smarthelp/emailUs.emailUs.errors'),
  LOGIN_PAGE_INSTRUCTIONS: i18n.message('smarthelp/emailUs.emailUs.loginInstr'),
};

class PrivacyEmailForm extends Component {
  componentDidUpdate(prevProps) {
    if (!prevProps.isEmailPage && this.props.isEmailPage) {
      trackPage({ id: 'privacyWebformShown', link: 'smarthelp-privacy-webform' });
    }
  }

  messageUsContainer() {
    return (
      <ContactOption
        key="message"
        id="contact-message-option"
        contactLinkCSS="btn vx_btn-link contact-link"
        iconClass="contact-panel__vx_btn-link_icon-message"
        text={emailLabels.MESSAGE_HEADER}
        message={emailLabels.MESSAGE_TEXT}
        supportMessageCSS="support-message"
        clickAction={() => this.messageUsClickHandler()}
      />
    );
  }

  /**
   * Parsing contact-us/help center links
   */
  parseHcrContent(linkContent) {
    try {
      if (!linkContent) {
        return;
      }

      const contentLinks = linkContent.querySelectorAll('a[href]');
      Array.from(contentLinks).forEach((link) => {
        const innerText = link.innerText || '';
        link.addEventListener('click', (e) => this.contactLinkClickHandler(e, innerText));
      });
    } catch (e) {
      // suppress error
    }
  }

  /**
   * When contact us/home page link is clicked on email page
   */
  contactLinkClickHandler(event, innerText) {
    event.stopImmediatePropagation();
    const { kanaCode } = this.props;
    trackLink({
      id: 'privacyWebformContentClicked',
      link: `smarthelp-${innerText}-link-clicked-on-privacy-webform-Page|${kanaCode}`,
    });
  }

  /**
   * Parsing message us link during logout
   */
  parseMessageUsContent(linkContent) {
    try {
      if (!linkContent) {
        return;
      }
      const contentLinks = linkContent.querySelectorAll('a[href]');
      Array.from(contentLinks).forEach((link) => {
        link.addEventListener('click', (e) => this.messageUsClickHandler(e));
        link.removeAttribute('href');
      });
    } catch (e) {
      // suppress error
    }
  }

  /**
   * When message us option is clicked in both logout/login
   */
  messageUsClickHandler(event) {
    const { kanaCode } = this.props;
    trackLink({ id: 'privacyWebformMesssageUsClicked', link: `message-us-option-clicked|privacyFormPage|${kanaCode}` });

    // Logged Out on MessageUs Link clicked
    if (!Page.isChatBotEnabled()) {
      // As message us is available only as a link during logout, to stop rendering multiple times and window reload
      event.preventDefault();
      event.stopImmediatePropagation();
      return this.props.loadModalContainer(modalChannels.messageUs, '', false, true);
    }

    // Logged In MessageUs Option Clicked
    // If chat/message us window already exists return null
    if (isNodeChatAlreadyExists()) {
      return null;
    }

    // Logged In - open message us
    return initiateAsyncChat({ topicRefId: kanaCode, origin: asyncOrigins.PRIVACY_PAGE_MESSAGE_US });
  }

  /**
   * When login link or button is clicked
   */
  loginClickHandler(event, type) {
    if (type === 'link') {
      // To restrict this function to execute once when clicked on login link
      // And button click and events to stop further propagation
      event.stopImmediatePropagation();
    }
    const { kanaCode } = this.props;
    trackLink({ id: 'privacyWebformLoginClicked', link: `email-login-${type}-privacy-webform|${kanaCode}` });
    const currentURI = Page.getCurrentLocationURL();
    this.props.loginFromEmailPage();
    this.props.dispatchLogin(currentURI);
  }

  /**
   * Parsing login link
   */
  parseLinkContent(linkContent) {
    try {
      if (!linkContent) {
        return;
      }

      const contentLinks = linkContent.querySelectorAll('a[href]');
      Array.from(contentLinks).forEach((link) => {
        link.addEventListener('click', (e) => this.loginClickHandler(e, 'link'));
      });
    } catch (e) {
      // suppress error
    }
  }

  /**
   * Logout webForm link clicked function
   */
  logoutWebFormLinkClick() {
    trackLink({ id: 'privacyWebformNoAccountLoginClicked', link: 'log-out-privacy-webForm-link-clicked' });
    this.props.showEmailWebForm();
  }

  /**
   * Function to display information on page during logout
   */
  renderLogOutInstructions() {
    const pageInfoAttrs = {
      className: 'email_privacy__details__login_text',
    };

    const innerHTML = Page.getHTMLElement({ message: emailLabels.LOGIN_INSTRUCTIONS, useHTML: true });
    return (
      <p
        {...pageInfoAttrs}
        ref={(el) => {
          this.parseLinkContent(el);
        }}
      >
        {innerHTML}
      </p>
    );
  }

  /**
   * Function checks for messageus availability
   * @returns {boolean}
   */
  renderMessageUsInstructions() {
    const { contactChannels, isIdiServiceLoaded } = this.props;
    if (isIdiServiceLoaded) {
      // After IDI Service is loaded check for message us availability
      if (isMessageUsAvailable(contactChannels)) {
        const pageInfoAttrs = {
          className: 'email_privacy__details__message_text',
        };

        const innerHTML = Page.getHTMLElement({ message: emailLabels.MESSAGE_US_INSTRUCTIONS, useHTML: true });
        return (
          <p
            {...pageInfoAttrs}
            ref={(el) => {
              this.parseMessageUsContent(el);
            }}
          >
            {innerHTML}
          </p>
        );
      }
      return <div />;
    }
    return <div />;
  }

  /**
   * Function to render hardcoded webForm or message us option and
   * opens async chat window during logout to login if message us is clicked
   */
  renderContactContainer() {
    const { contactChannels, isIdiServiceLoaded, kanaCode, fromChannel, isEmailPage } = this.props;
    if (isIdiServiceLoaded) {
      // After IDI Service is loaded check for message us availability
      if (isMessageUsAvailable(contactChannels)) {
        // Opens async chat during logout to login after message us click
        // logout to login will be true only if fromChannel exists
        // and only for email page open async chat
        if (fromChannel === modalChannels.messageUs && isEmailPage) {
          initiateAsyncChat({ topicRefId: kanaCode, origin: asyncOrigins.PRIVACY_PAGE_MESSAGE_US });
        }
        return this.messageUsContainer();
      }
      // render hard code webForm if message us is available
      return <EmailForm />;
    }
    return <div />;
  }

  /**
   * Function to display information to navigate to contact-us/home page
   */
  renderHelpCenterInstructions() {
    const pageInfoAttrs = {
      className: 'email_privacy__details__hcr_text',
    };

    const innerHTML = Page.getHTMLElement({ message: emailLabels.HELP_CENTER_INSTRUCTIONS, useHTML: true });
    return (
      <p
        {...pageInfoAttrs}
        ref={(el) => {
          this.parseHcrContent(el);
        }}
      >
        {innerHTML}
      </p>
    );
  }

  /**
   * Function displays Instructions for the page in logout/login
   */
  renderLogoutLoginInstructions() {
    if (Page.isLoggedIn()) {
      return (
        <div>
          <p className="email_privacy__details__login_text">{emailLabels.LOGIN_PAGE_INSTRUCTIONS}</p>
          {this.renderHelpCenterInstructions()}
        </div>
      );
    }

    return (
      <Fragment>
        {this.renderLogOutInstructions()}
        {this.renderMessageUsInstructions()}
        {this.renderHelpCenterInstructions()}
        <div className="email_privacy__details__login_btn">
          <Button data-testid="sh-privacy-login-btn" onClick={(e) => this.loginClickHandler(e, 'button')}>
            {emailLabels.LOGIN_TEXT}
          </Button>
        </div>
      </Fragment>
    );
  }

  /**
   * Logout webForm link
   */
  renderLoggedOutWebFormLink() {
    const { isPrivacyWebFormEnabled } = this.props;
    if (isPrivacyWebFormEnabled) {
      return <EmailForm />;
    }
    return (
      <a className="email_privacy__logout__webform_link" onClick={() => this.logoutWebFormLinkClick()}>
        {emailLabels.NO_ACCOUNT_INSTRUCTIONS}
      </a>
    );
  }

  /**
   * Function displays message us/logout webform link
   */
  renderContactComponent() {
    if (Page.isLoggedIn()) {
      return (
        <div>
          <div className="email_privacy__line" />
          {this.renderContactContainer()}
        </div>
      );
    }
    return <div className="email_privacy__logout">{this.renderLoggedOutWebFormLink()}</div>;
  }

  renderNewPrivacyPage() {
    const privacyClassName = Page.isLoggedIn() ? 'email_privacy__details login_email' : 'email_privacy__details';
    return (
      <div className="email_privacy">
        <div className={`${privacyClassName}`}>
          <div className="email_privacy__details__page_header">{emailLabels.WEB_FORM_HEADER}</div>
          {this.renderLogoutLoginInstructions()}
        </div>
        {this.renderContactComponent()}
      </div>
    );
  }

  render() {
    const { error, kanaCode } = this.props;
    if (error) {
      return (
        <div className="error-email-no-intent">
          <div className="vx_icon vx_icon-large vx_icon-critical-large test" />
          <div className="error-message-email-no-intent">{emailLabels.ERRORS.message}</div>
        </div>
      );
    }
    if (isPrivacyWebFormEnabledInRC(kanaCode)) {
      return this.renderNewPrivacyPage();
    }
    return null;
  }
}

function mapStateToProps(state) {
  return {
    error: state.emailNoIntent.error,
    kanaCode: state.emailNoIntent.kanaCode,
    contactChannels: state.contactChannels.IDIAvailability,
    isPrivacyWebFormEnabled: state.emailNoIntent.isPrivacyWebFormEnabled,
    fromChannel: state.modal.fromChannel,
    isEmailPage: state.emailNoIntent.display,
    isEmailSubmissionError: state.emailNoIntent.error,
    isIdiServiceLoaded: state.contactChannels.isIdiServiceLoaded,
  };
}

PrivacyEmailForm.propTypes = {
  error: PropTypes.bool,
  kanaCode: PropTypes.string.isRequired,
  loadModalContainer: PropTypes.func.isRequired,
  dispatchLogin: PropTypes.func.isRequired,
  loginFromEmailPage: PropTypes.func.isRequired,
  isIdiServiceLoaded: PropTypes.bool.isRequired,
  contactChannels: PropTypes.arrayOf(PropTypes.shape(CONTACT_OPTION)),
  isEmailPage: PropTypes.bool.isRequired,
  fromChannel: PropTypes.string,
  showEmailWebForm: PropTypes.func.isRequired,
  isPrivacyWebFormEnabled: PropTypes.bool.isRequired,
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      loadModalContainer,
      dispatchLogin,
      loginFromEmailPage,
      showEmailWebForm,
    },
    dispatch,
  );
}

PrivacyEmailForm.defaultProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(PrivacyEmailForm);
